// src/data/vanillaTemplates.ts

// ============================================================================
// TRENCH CRUSADE - GENERAL ARMOURY (COMPLETE)
// ============================================================================

export interface PremadeItemTemplate {
  name: string;
  category: string;
  keywords: string[];
  range?: string;
  armourModifier?: string;
  notes?: string;
  cost: number;
  costCurrency: "ducats" | "glory" | "free";
  limit?: number;
  limitScope?: 'name' | 'templateName';
  restrictions?: string[];
}

export const GENERAL_ARMOURY_ITEMS: PremadeItemTemplate[] = [
  // ============================================================================
  // RANGED WEAPONS
  // ============================================================================
  {
    name: "Anti-Materiel Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY", "IGNORE ARMOUR"],
    range: "36\"",
    cost: 3,
    costCurrency: "glory",
    limit: 1,
    notes: ""
  },
  {
    name: "Automatic Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "ASSAULT", "AUTOMATIC 2", "PISTOL"],
    range: "12\"/Melee",
    cost: 20,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "AUTOMATIC 2", "Bayonet Lug"],
    range: "24\"",
    cost: 40,
    costCurrency: "ducats",
    limit: 2,
    notes: "Focused Fire: When this Weapon is used to make 2 Ranged Attacks, the same enemy model must be the target of both attacks."
  },
  {
    name: "Automatic Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "ASSAULT", "SHOTGUN", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Blunderbuss",
    category: "ranged",
    keywords: ["2-HANDED", "SHRAPNEL", "Shield Combo"],
    range: "10\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Bolt-Action Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "Bayonet Lug"],
    range: "24\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "FIRE", "FLAMETHROWER", "IGNORE ARMOUR"],
    range: "8\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 3,
    notes: ""
  },
  {
    name: "Grenade Launcher",
    category: "ranged",
    keywords: ["2-HANDED", "BLAST 3\"", "HEAVY", "IGNORE COVER", "SHRAPNEL"],
    range: "36\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Heavy Flamethrower",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "AUTOMATIC 2", "FIRE", "FLAMETHROWER", "HEAVY", "IGNORE ARMOUR"],
    range: "10\"",
    cost: 55,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Heavy Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "+1 INJURY DICE", "HEAVY", "SHOTGUN", "Shield Combo"],
    range: "12\"",
    cost: 20,
    costCurrency: "ducats",
    limit: 2,
    notes: "Tungsten-orichalcum Alloy Shot: Add +1 INJURY DICE to rolls for Ranged Attacks made by this Weapon at Short Range."
  },
  {
    name: "Machine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "AUTOMATIC 3", "HEAVY", "RELOAD"],
    range: "36\"",
    cost: 50,
    costCurrency: "ducats",
    limit: 2,
    notes: ""
  },
  {
    name: "Musket",
    category: "ranged",
    keywords: ["2-HANDED", "-1 INJURY DICE", "Bayonet Lug"],
    range: "18\"",
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "PISTOL"],
    range: "12\"/Melee",
    cost: 6,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Semi-Automatic Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug"],
    range: "24\"",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Shotgun",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "SHOTGUN", "Bayonet Lug", "Shield Combo"],
    range: "12\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Silenced Pistol",
    category: "ranged",
    keywords: ["1-HANDED", "ASSAULT", "PISTOL"],
    range: "12\"/Melee",
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: "Silent: Add +1 DICE to the roll if the attacker is in contact with a terrain piece at least ½\" tall between it and target."
  },
  {
    name: "Sniper Rifle",
    category: "ranged",
    keywords: ["2-HANDED", "+1 DICE", "CRITICAL", "RISKY"],
    range: "48\"",
    cost: 35,
    costCurrency: "ducats",
    limit: 3,
    notes: "Bull's Eye: If Success Roll is Critical Success, Injury Roll has IGNORE ARMOUR Keyword."
  },
  {
    name: "Submachine Gun",
    category: "ranged",
    keywords: ["2-HANDED", "ASSAULT", "Bayonet Lug", "Shield Combo"],
    range: "16\"",
    cost: 30,
    costCurrency: "ducats",
    limit: 2,
    notes: "Quick Bursts: Can take two Shoot ACTIONS during same Activation with this weapon."
  },

  // ============================================================================
  // MELEE WEAPONS
  // ============================================================================
  {
    name: "Anti-Tank Hammer",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "IGNORE ARMOUR", "RISKY"],
    range: "Melee",
    cost: 35,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["ELITE only"],
    notes: "Dangerous: Place 1 BLOOD MARKER next to model if Melee Attack Success Roll is Success or Critical Success."
  },
  {
    name: "Bayonet",
    category: "melee",
    keywords: ["2-HANDED", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 2,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Hammer/Maul",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY MODIFIER", "HEAVY"],
    range: "Melee",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Great Sword/Axe",
    category: "melee",
    keywords: ["2-HANDED", "+1 INJURY DICE", "CRITICAL", "HEAVY"],
    range: "Melee",
    cost: 12,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Misericordia",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["Combat Medic only"],
    notes: "Despatch: This weapon has IGNORE ARMOUR Keyword if target is Down."
  },
  {
    name: "Flail/Scourge",
    category: "melee",
    keywords: ["1-HANDED", "+1 DICE"],
    range: "Melee",
    cost: 5,
    costCurrency: "ducats",
    notes: "Unwieldy: The +1 DICE Keyword does not apply when used as Off-Hand Weapon."
  },
  {
    name: "Polearm",
    category: "melee",
    keywords: ["2-HANDED", "BLOCK", "CUMBERSOME", "Shield Combo"],
    range: "Melee",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Sword/Axe",
    category: "melee",
    keywords: ["1-HANDED", "CRITICAL"],
    range: "Melee",
    cost: 4,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Club",
    category: "melee",
    keywords: ["1-HANDED"],
    range: "Melee",
    cost: 3,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Trench Knife",
    category: "melee",
    keywords: ["1-HANDED", "-1 DICE"],
    range: "Melee",
    cost: 1,
    costCurrency: "ducats",
    notes: ""
  },

  // ============================================================================
  // GRENADES
  // ============================================================================
  {
    name: "Frag Grenades",
    category: "grenade",
    keywords: ["GRENADE", "ASSAULT", "BLAST 2\"", "IGNORE COVER", "IGNORE LONG RANGE", "SHRAPNEL"],
    range: "8\"",
    cost: 7,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Gas Grenades",
    category: "grenade",
    keywords: ["GRENADE", "-1 INJURY DICE", "ASSAULT", "BLAST 3\"", "GAS", "IGNORE ARMOUR", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Incendiary Grenades",
    category: "grenade",
    keywords: ["GRENADE", "ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "8\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 2,
    notes: "Liquid Fire: If Success Roll is Critical Success, Injury Roll has IGNORE ARMOUR Keyword."
  },
  {
    name: "Molotov Cocktail",
    category: "grenade",
    keywords: ["GRENADE", "-1 INJURY DICE", "ASSAULT", "FIRE", "IGNORE COVER", "IGNORE LONG RANGE"],
    range: "6\"",
    cost: 5,
    costCurrency: "ducats",
    notes: "Liquid Fire: If Success Roll is Critical Success, Injury Roll has IGNORE ARMOUR Keyword."
  },
  {
    name: "Satchel Charge",
    category: "grenade",
    keywords: ["GRENADE", "+1 INJURY DICE", "BLAST 3\"", "CONSUMABLE", "HEAVY", "IGNORE ARMOUR", "IGNORE COVER", "SCATTER"],
    range: "6\"",
    cost: 15,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["1 per model"],
    notes: "Heavy Explosive: Once during a game, a model with Satchel Charge can use it to make a Ranged Attack."
  },

  // ============================================================================
  // SHIELDS
  // ============================================================================
  {
    name: "Trench Shield",
    category: "shield",
    keywords: ["Shield Combo"],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 10,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Heavy Ballistic Shield",
    category: "shield",
    keywords: ["Shield Combo"],
    cost: 15,
    costCurrency: "ducats",
    restrictions: ["Models wearing Machine Armour only"],
    notes: ""
  },

  // ============================================================================
  // ARMOUR
  // ============================================================================
  {
    name: "Standard Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-1 INJURY MODIFIER",
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Reinforced Armour",
    category: "armour",
    keywords: [],
    armourModifier: "-2 INJURY MODIFIER",
    cost: 40,
    costCurrency: "ducats",
    notes: "ELITE & Mechanized Heavy Infantry only"
  },
  {
    name: "Machine Armour",
    category: "armour",
    keywords: [],
    cost: 50,
    costCurrency: "ducats",
    limit: 1,
    restrictions: ["ELITE & Mechanized Heavy Infantry only", "Limit: 1 excluding Mechanized Heavy Infantry"],
    notes: ""
  },
  {
    name: "Engineer Body Armour",
    category: "armour",
    keywords: [],
    cost: 45,
    costCurrency: "ducats",
    restrictions: ["Combat Engineer only"],
    notes: ""
  },

  // ============================================================================
  // EQUIPMENT
  // ============================================================================
  {
    name: "Binoculars",
    category: "equipment",
    keywords: [],
    cost: 10,
    costCurrency: "ducats",
    restrictions: ["ELITE only"],
    notes: "Binoculars: Enemy models with INFILTRATOR Keyword cannot deploy within 16\" unless in own deployment zone."
  },
  {
    name: "Combat Helmet",
    category: "equipment",
    keywords: ["NEGATE SHRAPNEL", "Headgear"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Field Shrine",
    category: "equipment",
    keywords: [],
    cost: 2,
    costCurrency: "glory",
    notes: "Shrine: After deployment, set up Field Shrine terrain piece (½\" tall, 40mm base) in deployment zone. Impassable terrain. In Morale Phase, each friendly Field Shrine adds 3 to model count (max +9). Tear It Down!: Models can attack Field Shrine. If hit, remove from battlefield."
  },
  {
    name: "Gas Mask",
    category: "equipment",
    keywords: ["NEGATE GAS"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Martyrdom Pills",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 20,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["ELITE only"],
    notes: "Inured to Pain: When deployed, can consume for NEGATE FEAR and -1 INJURY DICE to attacks hitting model until end of game."
  },
  {
    name: "Medi-kit",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "Treat ACTION: Risky Success Roll. Success: Remove 1 BLOOD MARKER from self/friendly within 1\" OR stand up friendly Down model within 1\"."
  },
  {
    name: "Mountaineer Kit",
    category: "equipment",
    keywords: [],
    cost: 3,
    costCurrency: "ducats",
    limit: 4,
    notes: "Mountaineer: Add +1 DICE to Risky Success Rolls when attempting to climb sheer surface."
  },
  {
    name: "Musical Instrument",
    category: "equipment",
    keywords: ["HELD"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "Fanfare: Add +1 DICE to Risky Success Rolls for friendly models taking Dash ACTION within 4\"."
  },
  {
    name: "Shovel",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    notes: "Shovel: Model starting on Open terrain has COVER Keyword until moving from starting position. Can use as 2-Handed Melee Weapon instead of other Melee Weapons."
  },
  {
    name: "Troop Flag",
    category: "equipment",
    keywords: ["LEADER", "HELD"],
    cost: 1,
    costCurrency: "glory",
    limit: 1,
    notes: ""
  },
  {
    name: "Holy Relic",
    category: "equipment",
    keywords: ["BLESSED 1"],
    cost: 2,
    costCurrency: "glory",
    restrictions: ["ELITE only"],
    notes: ""
  },
  {
    name: "Unholy Relic",
    category: "equipment",
    keywords: ["FEAR"],
    cost: 15,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Unholy Trinket",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    notes: "Talisman: Once per game, when Risky Success Roll is Failure, can use trinket to prevent Activation from ending."
  },
  {
    name: "Blessed Icon",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    notes: "Talisman: Once per game, when Risky Success Roll is Failure, can use icon to prevent Activation from ending."
  },
  {
    name: "Incendiary Ammunition",
    category: "equipment",
    keywords: ["CONSUMABLE"],
    cost: 15,
    costCurrency: "ducats",
    limit: 1,
    notes: "Incendiary: Choose 1 Ranged Weapon without BLAST, FIRE, GAS, or SHRAPNEL. Weapon gains FIRE Keyword."
  },
  {
    name: "Infernal Brand",
    category: "equipment",
    keywords: ["NEGATE FIRE"],
    cost: 5,
    costCurrency: "ducats",
    notes: ""
  },
  {
    name: "Hellbound Soul Contract",
    category: "equipment",
    keywords: [],
    cost: 5,
    costCurrency: "ducats",
    limit: 3,
    restrictions: ["Heretic Troopers & Legionnaires only"],
    notes: "Contract: If taken Out of Action, add 1 BLOOD MARKER to each enemy within 1\" (NEGATE FIRE models unaffected)."
  }
];

export const FACTION_NAMES = [
  "Trench Pilgrims",
  "New Antioch",
  "Iron Sultanate",
  "Heretic Legions",
  "Black Grail",
  "The Court",
  "Mercenaries",
];
